import type {
  ApprovalWorkflowListResponseApi,
  ApprovalWorkflowResponseApi,
  ApprovalWorkflowStageResponseApi,
  CreateApprovalWorkflowRequestApi,
  CreateApprovalWorkflowStageRequestApi,
  UpdateApprovalWorkflowRequestApi,
  UpdateApprovalWorkflowStageRequestApi,
  WorkflowStatusApi,
} from "../models/approvalWorkflow";

export type WorkflowStatus = WorkflowStatusApi; // re-export for consumers

import type { UserListItemApi } from "../models/user";

export interface WorkflowAssignee {
  id: string;
  type: "named";
  userId: string;
  name: string;
  email: string;
}

export type ApprovalRule = "any" | "all";

export interface WorkflowStage {
  id: string;
  name: string;
  order: number;
  assignees: WorkflowAssignee[];
  rule: ApprovalRule;
  slaDays: number | null;
  escalateToUserId: string | null;
  escalateToName: string | null;
}

export interface RoutingRule {
  minNetImpact: number | null;
  maxNetImpact: number | null;
  categories: string[];
}

export interface WorkflowDefinition {
  id: string;
  name: string;
  description: string;
  isDefault: boolean;
  /** Three-state lifecycle: active | suspended | inactive */
  status: WorkflowStatus;
  /** True once any scenario has ever been submitted into this workflow — disables hard delete */
  hasBeenUsed: boolean;
  /** When true, the scenario creator may approve their own scenario IF they are an assigned approver on that stage */
  allowSelfApproval: boolean;
  /** True once the notify-scenario-creators actions exist for the current deactivate attempt; reset when the workflow is deactivated */
  notifyDeactivateActionsCreated: boolean;
  routing: RoutingRule;
  stages: WorkflowStage[];
}

function resolveUser(
  userId: string,
  users: UserListItemApi[],
): { name: string; email: string } {
  const user = users.find((u) => u.userId === userId);
  return {
    name: user?.name ?? `User ${userId}`,
    email: user?.email ?? "",
  };
}

function mapStageFromApi(
  stage: ApprovalWorkflowStageResponseApi,
  users: UserListItemApi[],
): WorkflowStage {
  const escalateUser = stage.escalateToUserId
    ? resolveUser(stage.escalateToUserId, users)
    : null;

  return {
    id: String(stage.id),
    name: stage.name,
    order: stage.stageOrder,
    assignees: stage.assignees.map((a) => {
      const resolved = resolveUser(a.userId, users);
      return {
        id: String(a.id),
        type: "named" as const,
        userId: a.userId,
        name: resolved.name,
        email: resolved.email,
      };
    }),
    rule: stage.approvalRule === "all" ? "all" : "any",
    slaDays: stage.slaDays || null,
    escalateToUserId: stage.escalateToUserId ?? null,
    escalateToName: escalateUser?.name ?? null,
  };
}

/**
 * The API persists two flags rather than a status enum; suspension only ever applies to
 * an active workflow.
 */
function deriveStatus(isActive: boolean, isSuspended: boolean): WorkflowStatus {
  if (!isActive) return "inactive";
  return isSuspended ? "suspended" : "active";
}

export function mapWorkflowFromApi(
  workflow: ApprovalWorkflowResponseApi,
  users: UserListItemApi[],
): WorkflowDefinition {
  return {
    id: String(workflow.id),
    name: workflow.name,
    description: workflow.description ?? "",
    isDefault: workflow.isDefault,
    status:
      workflow.status ?? deriveStatus(workflow.isActive, workflow.isSuspended),
    hasBeenUsed: workflow.hasBeenUsed ?? false,
    allowSelfApproval: workflow.allowSelfApproval,
    notifyDeactivateActionsCreated:
      workflow.notifyDeactivateActionsCreated ?? false,
    routing: {
      minNetImpact:
        workflow.minNetImpact != null ? Number(workflow.minNetImpact) : null,
      maxNetImpact:
        workflow.maxNetImpact != null ? Number(workflow.maxNetImpact) : null,
      categories: workflow.scenarioCategory ? [workflow.scenarioCategory] : [],
    },
    stages: workflow.stages
      .slice()
      .sort((a, b) => a.stageOrder - b.stageOrder)
      .map((s) => mapStageFromApi(s, users)),
  };
}

export function mapWorkflowsFromApi(
  response: ApprovalWorkflowListResponseApi,
  users: UserListItemApi[],
): WorkflowDefinition[] {
  return response.workflows.map((w) => mapWorkflowFromApi(w, users));
}

export function toCreateWorkflowRequest(
  wf: WorkflowDefinition,
): CreateApprovalWorkflowRequestApi {
  return {
    name: wf.name,
    description: wf.description || null,
    isDefault: wf.isDefault,
    isActive: wf.status !== "inactive",
    isSuspended: wf.status === "suspended",
    allowSelfApproval: wf.allowSelfApproval,
    notifyDeactivateActionsCreated: wf.notifyDeactivateActionsCreated,
    scenarioCategory:
      wf.routing.categories.length > 0 ? wf.routing.categories[0] : null,
    minNetImpact: wf.routing.minNetImpact,
    maxNetImpact: wf.routing.maxNetImpact,
  };
}

export function toUpdateWorkflowRequest(
  wf: WorkflowDefinition,
): UpdateApprovalWorkflowRequestApi {
  return {
    name: wf.name,
    description: wf.description || null,
    isDefault: wf.isDefault,
    isActive: wf.status !== "inactive",
    isSuspended: wf.status === "suspended",
    allowSelfApproval: wf.allowSelfApproval,
    notifyDeactivateActionsCreated: wf.notifyDeactivateActionsCreated,
    scenarioCategory:
      wf.routing.categories.length > 0 ? wf.routing.categories[0] : null,
    minNetImpact: wf.routing.minNetImpact,
    maxNetImpact: wf.routing.maxNetImpact,
  };
}

export function toCreateStageRequest(
  stage: WorkflowStage,
): CreateApprovalWorkflowStageRequestApi {
  return {
    name: stage.name,
    stageOrder: stage.order,
    approvalRule: stage.rule,
    slaDays: stage.slaDays,
    escalateToUserId: stage.escalateToUserId,
    assigneeUserIds: stage.assignees.map((a) => a.userId),
  };
}

export function toUpdateStageRequest(
  stage: WorkflowStage,
): UpdateApprovalWorkflowStageRequestApi {
  return {
    name: stage.name,
    stageOrder: stage.order,
    approvalRule: stage.rule,
    slaDays: stage.slaDays,
    escalateToUserId: stage.escalateToUserId,
    assigneeUserIds: stage.assignees.map((a) => a.userId),
  };
}
